import React, { useEffect, useState, useMemo } from 'react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import randomString from 'random-string';
import { useQuery, useMutation } from '@apollo/client';

import { userRole } from '../../constant';

import {
    ProviderInfoCard,
    NoteCard,
    ContainedButton,
    Text,
    Title,
    Header,
    Progress,
    SessionDetailCard,
    RulesCard,
    PDPADialog,
} from '../../components/index';

import { patientPageWidth, GROUP_RULES_TH, OPEN_RULES_TH } from '../../constant';

import socket from '../../socket';

import { GET_SESSION_DETAIL, UPDATE_MY_NOTE, checkIn, canJoin } from '../../services/patient';
import { joinRoom } from '../../services/index';

import { alertError, alertSuccess } from '../../redux/stateActions';

const STATUS = Object.freeze({
    CHECK_IN: 'CHECK_IN',
    WAITING: 'WAITING',
    ON_CALLING: 'ON_CALLING',
    NOTE: 'NOTE',
    SUCCESS: 'SUCCESS',
});

const GroupOpenSessionDetail = ({ history }) => {
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));
    const dispatch = useDispatch();

    const { id } = useParams();
    const user = useSelector((state) => state.user);

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(STATUS.CHECK_IN);
    const [note, setNote] = useState('');

    const handleGoback = () => {
        history.goBack();
    };

    const handleGoHome = () => {
        history.push('/home');
    };

    /*--------------- Get Group Session ---------------*/
    const [sessionData, setSessionData] = useState({});
    const [rules, setRules] = useState([]);
    const [patientSummaryData, setPatientSummaryData] = useState({});

    const header = useMemo(() => {
        switch (status) {
            case STATUS.CHECK_IN:
            case STATUS.WAITING:
            case STATUS.ON_CALLING:
                return {
                    title: sessionData.meet?.type === 'group' ? 'Group Session' : 'Open Session',
                    showNotice: true,
                    showMessage: true,
                };
            case STATUS.NOTE:
                return { title: 'Summary', showNotice: false, showMessage: false };
            case STATUS.SUCCESS:
                return { title: 'Summary', showNotice: false, showMessage: false };
        }
    }, [status, sessionData]);

    const {
        data: getSessionData,
        error: getSessionError,
        loading: getSessionLoading,
        refetch: getSessionRefetch,
    } = useQuery(GET_SESSION_DETAIL, {
        variables: { id: id, patient: user.patient.id },
        skip: _.isEmpty(user) || !id,
        pollInterval: 30000,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getSessionError) {
            dispatch(alertError(`Cannot get Session: ${getSessionError.message}`));
            handleGoHome();
        }

        if (getSessionData && _.isEmpty(getSessionData.session)) {
            handleGoHome();
        }

        if (getSessionData && !_.isEmpty(getSessionData.session)) {
            setSessionData(getSessionData.session);

            const { meet, patient_summaries, actualEndTime, roomId, sessionEnd, cancelled } =
                getSessionData.session;

            if (meet.type === 'group') {
                setRules(GROUP_RULES_TH.filter((r) => meet.rules.includes(r.id)));

                const foundPatientSummary = patient_summaries.find(
                    (summary) => summary.patient.id === user.patient.id
                );

                setPatientSummaryData(foundPatientSummary);

                // patient summary not found
                if (!foundPatientSummary) {
                    dispatch(alertError(`Unauthorized Session`));
                    handleGoHome();
                    return;
                }

                // check if session cancelled or ended
                if (cancelled) {
                    dispatch(alertError(`Session Cancelled`));
                    handleGoHome();
                    return;
                }
                if (sessionEnd && foundPatientSummary.myNote) {
                    dispatch(alertSuccess(`Session Ended`));
                    handleGoHome();
                    return;
                }

                // check if my note already set
                if (foundPatientSummary.myNote) {
                    return setStatus(STATUS.SUCCESS);
                }

                // check if videos call has end
                if (actualEndTime) {
                    return setStatus(STATUS.NOTE);
                }

                // room already exist
                if (roomId) {
                    return setStatus(STATUS.ON_CALLING);
                }

                // check if alreay checked-in
                if (foundPatientSummary.checkinTime) {
                    return setStatus(STATUS.WAITING);
                }
            }

            if (meet.type === 'open') {
                setRules(OPEN_RULES_TH.filter((r) => meet.rules.includes(r.id)));

                if (meet.sessionType === 'invite') {
                    const foundPatientSummary = patient_summaries.find(
                        (summary) => summary.patient.id === user.patient.id
                    );

                    setPatientSummaryData(foundPatientSummary);

                    // patient summary not found
                    if (!foundPatientSummary) {
                        dispatch(alertError(`Unauthorized Session`));
                        handleGoHome();
                        return;
                    }
                }

                // check if videos call has end
                if (actualEndTime && sessionEnd) {
                    return setStatus(STATUS.SUCCESS);
                }

                if (roomId) {
                    return setStatus(STATUS.ON_CALLING);
                }

                return setStatus(STATUS.WAITING);
            }
        }
    }, [getSessionError, getSessionData]);

    /*--------------- PDPA ---------------*/
    const [openPDPA, setOpenPDPA] = useState(false);
    const [accepted, setAccepted] = useState(false);

    const onCheckChange = (e) => {
        setAccepted(e.target.checked);
    };

    const handleClosePDPA = () => {
        setOpenPDPA(false);
        setAccepted(false);
    };

    const onAccept = () => {
        setOpenPDPA(false);

        if (status === STATUS.CHECK_IN) {
            handleCheckIn();
        }

        if (status === STATUS.WAITING || status === STATUS.ON_CALLING) {
            handleJoinRoom();
        }
    };

    const handleJoinRoom = async () => {
        if (!accepted) {
            setOpenPDPA(true);
            return;
        }

        setLoading(true);

        try {
            if (sessionData.meet.type === 'open') {
                const canJoinRes = await canJoin({
                    sessionId: sessionData.id,
                    patientId: user.patient.id,
                });

                if (canJoinRes.data) {
                    if (canJoinRes.data.canJoin === false) {
                        setLoading(false);
                        return dispatch(alertError(`session is full.`));
                    }
                } else {
                    throw new Error(canJoinRes);
                }
            }

            const params = new URLSearchParams();
            params.append('roomId', sessionData.roomId);
            params.append('sessionId', randomString({ length: 16 }).toLowerCase());
            params.append('role', userRole.PATIENT);
            params.append(
                'displayName',
                `${user.fullname}|${user.patient?.id}|${user.avatar?.url}|Patient`
            );
            params.append('password', '');

            const joinRes = await joinRoom(sessionData.roomId, params);

            if (joinRes.data) {
                // history.replace({
                //     pathname: `/${sessionData?.meet?.type === 'group' ? 'groupcall' : 'opencall'}/${
                //         sessionData.id
                //     }`,
                //     search: `?roomId=${joinRes.data.roomId}&session=${joinRes.data.session}`,
                // });

                window.location.href = `${window.location.origin}/${
                    sessionData?.meet?.type === 'group' ? 'groupcall' : 'opencall'
                }/${sessionData.id}?roomId=${joinRes.data.roomId}&session=${joinRes.data.session}`;
            } else {
                throw new Error(joinRes);
            }
        } catch (error) {
            console.log('group open ', error);
            dispatch(
                alertError(`Cannot join group open session: ${error.response?.data?.message}`)
            );
            setLoading(false);
        }
    };

    /*--------------- Check-In ---------------*/
    const handleCheckIn = async () => {
        if (!accepted) {
            setOpenPDPA(true);
            return;
        }

        setLoading(true);

        try {
            const res = await checkIn({
                patientSummaryId: patientSummaryData.id,
                sessionId: sessionData.id,
                meetId: sessionData.meet.id,
                patientId: user.patient.id,
            });

            if (res.status === 200) {
                setLoading(false);
                getSessionRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`Cannot check-in: ${error.message}`));
            setLoading(false);
        }
    };

    /*--------------- Update My Note ---------------*/
    const [
        updateMyNote,
        { data: updateMyNoteData, error: updateMyNoteError, loading: updateMyNoteLoading },
    ] = useMutation(UPDATE_MY_NOTE);

    useEffect(() => {
        if (updateMyNoteError) {
            dispatch(alertError(`Cannot update My Note: ${updateMyNoteError.message}`));
        }

        if (updateMyNoteData) {
            setStatus(STATUS.SUCCESS);
        }
    }, [updateMyNoteError, updateMyNoteData]);

    const handleNote = (value) => {
        setNote(value);
    };

    const handleSaveNote = () => {
        updateMyNote({
            variables: {
                id: patientSummaryData.id,
                myNote: note,
            },
        });
        setStatus(STATUS.SUCCESS);
    };

    useEffect(() => {
        // fire after provider finished set up group room
        socket.on('providerSetUpGroupRoom', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });

        // fire after first render. for open session
        socket.emit('joinOpenSession', { sessionId: id });

        // fire after provider finished set up open room
        socket.on('providerSetUpOpenRoom', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });

        socket.on('sessionEnd', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });
        socket.on('sessionUpdated', (data) => {
            if (data?.sessionId === id) {
                getSessionRefetch();
            }
        });
    }, []);

    return (
        <div
            style={{
                padding: '0 15px 100px',
                width: '100%',
                maxWidth: patientPageWidth,
            }}
        >
            <PDPADialog
                open={openPDPA}
                onClose={handleClosePDPA}
                accepted={accepted}
                onCheckChange={onCheckChange}
                onAccept={onAccept}
            />
            <Header
                showGoBack1
                onClickGoBack={handleGoback}
                showNotice={header?.showNotice}
                showMessage={header?.showMessage}
                childrenContainerStyle={{ textAlign: 'center' }}
                style={{ marginBottom: '20px' }}
            >
                <Text $bold>{header?.title}</Text>
            </Header>
            <div style={{ position: 'relative' }}>
                {getSessionLoading && <Progress />}
                {(status === STATUS.CHECK_IN ||
                    status === STATUS.WAITING ||
                    status === STATUS.ON_CALLING) && (
                    <div style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}>
                        <SessionDetailCard data={sessionData?.meet} showStaff>
                            <div>
                                {status === STATUS.CHECK_IN && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginTop: '10px',
                                        }}
                                    >
                                        <ContainedButton
                                            onClick={handleCheckIn}
                                            style={{ width: mdUp ? '300px' : '100%' }}
                                            loading={loading}
                                            disabled={
                                                _.isEmpty(sessionData) ||
                                                (!_.isEmpty(sessionData) &&
                                                    !dayjs().isBetween(
                                                        dayjs(sessionData.meet.date)
                                                            .set({
                                                                hour: sessionData.meet.startTime.split(
                                                                    ':'
                                                                )[0],
                                                                minute: sessionData.meet.startTime.split(
                                                                    ':'
                                                                )[1],
                                                            })
                                                            .subtract(10, 'minute'),
                                                        dayjs(sessionData.meet.date).set({
                                                            hour: sessionData.meet.endTime.split(
                                                                ':'
                                                            )[0],
                                                            minute: sessionData.meet.endTime.split(
                                                                ':'
                                                            )[1],
                                                        }),
                                                        'minute',
                                                        '()'
                                                    ))
                                            }
                                        >
                                            Check in
                                        </ContainedButton>
                                    </div>
                                )}
                                {(status === STATUS.WAITING || status === STATUS.ON_CALLING) && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginTop: '10px',
                                        }}
                                    >
                                        {sessionData?.meet?.type === 'group' && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    margin: '25px 0',
                                                }}
                                            >
                                                <Text $fontSize="20px">Check-in Successful</Text>
                                                <Text
                                                    $color="gray2"
                                                    style={{ textAlign: 'center' }}
                                                >
                                                    We’ll send you a notification when we’re almost
                                                    ready to see you.
                                                </Text>
                                            </div>
                                        )}
                                        <ContainedButton
                                            onClick={handleJoinRoom}
                                            loading={loading}
                                            disabled={
                                                _.isEmpty(sessionData) || status === STATUS.WAITING
                                            }
                                            style={{
                                                width: mdUp ? '300px' : '100%',
                                            }}
                                        >
                                            Join Now
                                        </ContainedButton>
                                    </div>
                                )}
                            </div>
                        </SessionDetailCard>
                        {!_.isEmpty(rules) && <RulesCard data={rules} />}
                    </div>
                )}

                {status === STATUS.NOTE && (
                    <div style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}>
                        <ProviderInfoCard data={sessionData?.meet?.staff} />
                        <NoteCard onChange={(event) => handleNote(event.target.value)} />

                        <div
                            style={{
                                marginTop: mdUp ? '20px' : '10px',
                                display: 'flex',
                                justifyContent: mdUp ? 'flex-end' : 'center',
                                position: mdUp ? 'static' : 'fixed',
                                bottom: 0,
                                left: 0,
                                width: mdUp ? 'auto' : '100%',
                                padding: mdUp ? 0 : '20px',
                                backgroundColor: mdUp ? 'transparent' : 'white',
                            }}
                        >
                            <ContainedButton
                                style={{ width: mdUp ? '240px' : '100%' }}
                                disabled={!note}
                                onClick={handleSaveNote}
                                loading={updateMyNoteLoading}
                            >
                                Next
                            </ContainedButton>
                        </div>
                    </div>
                )}

                {status === STATUS.SUCCESS && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '50px',
                        }}
                    >
                        <div style={{ maxWidth: '536px' }}>
                            <Title
                                style={{
                                    fontWeight: '700',
                                    fontSize: '32px',
                                    marginBottom: '45px',
                                }}
                            >
                                Session Successful
                            </Title>
                            <br />
                            <Text $fontSize="14px" $color="gray2">
                                You can check all the details on Medical Record page.
                            </Text>
                            <br />
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginTop: '45px',
                                }}
                            >
                                <ContainedButton
                                    style={{ maxWidth: '250px' }}
                                    onClick={handleGoHome}
                                >
                                    Back to home
                                </ContainedButton>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

GroupOpenSessionDetail.propTypes = {
    history: PropTypes.object,
};

export default GroupOpenSessionDetail;
